import { AuthService } from "src/app/core/auth/auth.service";
import {
  Component,
  OnInit,
  NgZone,
  OnDestroy,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from "@angular/core";
import { Router } from "@angular/router";
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import am4themes_animated from "@amcharts/amcharts4/themes/animated";
import { StorageService } from "src/app/core/helpers/storage.service";
import { AlertService } from "src/app/core/alert/alert.service";
import { BusinessService } from "../shared/business.service";
import { UserDetails } from "src/app/pages/shared/models/responses/user-details";
import { DashboardData } from "./model/dashboard-models";
import { TerminalService } from "../manage-terminals/terminal.service";
import { AllTerminals } from "../manage-terminals/models/terminals-models";
import { ManageMerchantsService } from "../manage-merchants/manage-merchants.service";
import { Merchant } from "../manage-merchants/interface/merchant";
import { SystemConfigService } from "../system-config/system-config.service";
import { environment } from "../../../../../environments/environment";
import { OemService } from "../oem-management/services/oem.service";
import { OEM } from "../oem-management/models/oem";
import { AppUsage, configuredDeviceByDeviceTypeResponse } from "../shared/models/business-dahsboard-details.model";

declare var $: any;

am4core.useTheme(am4themes_animated);
am4core.options.queue = true;
am4core.options.onlyShowOnViewport = true;
@Component({
  standalone: false,
  selector: "app-dashboard",
  templateUrl: "./dashboard.component.html",
  styleUrls: ["./dashboard.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class DashboardComponent implements OnInit, OnDestroy {
  username: any;
  permissions: any[] = [];
  series: any;
  marker: any;
  dashboardData!: DashboardData;
  isLoading = true;
  chart: any;
  terminalStatisticsChart: any;
  boundTerminalsChart: any;
  userDetails!: UserDetails;
  allTerminals: AllTerminals["data"] = [];
  allOems: OEM[] = [];
  totalOemCount: number = 0;
  terminalTotalCount: number = 0;
  totalMerchantCount: number = 0
  numberOfDays: number = 7;
  days = 7;
  configuredDeviceByDeviceType: configuredDeviceByDeviceTypeResponse[] = [];
  allAppUsage: AppUsage[] = [];
  allMerchants: Merchant["data"] = [];
  totalDevicesPerClient: any[] = [];
  allApps: any[] = [];
  totalAppCount: number = 0;
  terminalStatistics: any[] = [];
  boundTerminals: any[] = [];
  pageIndex: number = environment.PageIndex;
  pageSize: number = environment.PageSize;

  dailyBoundTerminals: any[] = [];
  dailyUnboundTerminals: any[] = [];
  hasBoundTerminalsLoaded = false;
  hasUnboundTerminalsLoaded = false;
  isDevicesChartLoading: boolean = true;
  isDevicesChartEmpty: boolean = false;
  isTerminalStatLoading: boolean = true;
  isTerminalChartEmpty: boolean = false;

  constructor(
    private storageService: StorageService,
    private authService: AuthService,
    private zone: NgZone,
    private alertService: AlertService,
    private cdr: ChangeDetectorRef,
    private businessService: BusinessService,
    private terminalService: TerminalService,
    private oemService: OemService,
    private merchantService: ManageMerchantsService,
    private sysConfig: SystemConfigService,
    private router: Router
  ) {
  }

  ngOnInit() {
    this.getUserDetails();
    this.getDashboardData();
    this.getAllTerminals();
    this.getAllOem();
    this.getAllMerchants();
    this.getAllApps();
    this.isDevicesChartLoading = true;
    this.isDevicesChartEmpty = false;
    this.hasBoundTerminalsLoaded = false;
    this.hasUnboundTerminalsLoaded = false;
    this.getWeeklyBoundDevices();
    this.getWeeklyUnboundDevices();
    this.getAppUsage();
    this.getConfiguredDevicesByDeviceType();
  }

  async getPermissions() {
    this.permissions = await this.authService.getPermissions();
    this.cdr.markForCheck();
  }

  getUserDetails() {
    this.userDetails = this.storageService.getCurrentUser()?.user as UserDetails;
  }

  getDashboardData() {
    this.isLoading = true;

    const dashboardDataObservable =
      this.userDetails.userRole === 'ROLE_SUPER_ADMIN'
        ? this.businessService.getSuperAdminDashboardData()
        : this.businessService.getDashboardData();

    dashboardDataObservable.subscribe(
      (resp) => {
        this.isLoading = false;
        this.dashboardData = resp;
        if (this.userDetails.userRole === 'ROLE_SUPER_ADMIN') {
          this.totalDevicesPerClient = Object.entries(
            this.dashboardData.totalConfiguredDevicesPerClient).map(([key, value]) => ({ name: key, value: value }));
        }
        this.cdr.markForCheck();
      },
      (error) => {
        this.handleDataFetchError(error);
      }
    );
  }

  initializeChart(data: { successful: number; failed: number }) {
    this.chart = am4core.create('chartdiv', am4charts.PieChart);

    this.chart.data = [
      {
        'Model Type': 'Successful',
        'number': data?.successful,
      },
      {
        'Model Type': 'Failed',
        'number': data?.failed,
      },
    ];

    const series = this.chart.series.push(new am4charts.PieSeries());
    this.chart.logo.disabled = true;
    series.colors.list = [
      am4core.color('#54BE47'),
      am4core.color('#F6AA1C')
    ];
    series.ticks.template.disabled = true;
    series.alignLabels = false;
    series.labels.template.text = '{value.percent.formatNumber(\'#.0\')}%';
    series.labels.template.radius = am4core.percent(-40);
    series.labels.template.fill = am4core.color('white');

    this.chart.legend = new am4charts.Legend();
    this.chart.legend.useDefaultMarker = true;
    const marker = this.chart.legend.markers.template.children.getIndex(0);
    marker.strokeWidth = 1;
    marker.strokeOpacity = 1;
    marker.stroke = am4core.color('#ccc');

    series.dataFields.value = 'number';
    series.dataFields.category = 'Model Type';

    this.chart.innerRadius = am4core.percent(60);

    // Create total label in the center
    const totalLabel = this.chart.chartContainer.createChild(am4core.Label);
    totalLabel.text = `Transactions\n${this.chart.numberFormatter.format(this.getTotal(), "#,###.##")}`;
    totalLabel.horizontalCenter = 'middle';
    totalLabel.verticalCenter = 'middle';
    totalLabel.textAlign = 'middle';
    totalLabel.fontSize = 18;
    totalLabel.fontWeight = 'bold';
    totalLabel.isMeasured = false; // prevents messing with chart layout
    totalLabel.x = this.chart.pixelWidth / 2;
    totalLabel.y = this.chart.pixelHeight / 2;

    // Ensure it stays centered when chart resizes
    this.chart.events.on('sizechanged', () => {
      totalLabel.x = this.chart.pixelWidth / 2;
      totalLabel.y = this.chart.pixelHeight / 2;
    });
  }

  getTotal() {
    return this.chart.data.reduce((sum: number, item: { number: number }) => sum + item.number, 0);
  }

  //terminal statistics chart

  initalizeTerminalStatisticsChart() {
    if (this.terminalStatisticsChart) {
      this.terminalStatisticsChart.dispose();
    }
    this.terminalStatisticsChart = am4core.create("terminalStatisticsDiv", am4charts.XYChart);
    this.terminalStatisticsChart.logo.disabled = true;
    this.terminalStatisticsChart.data = this.configuredDeviceByDeviceType;

    // Create axes
    let categoryAxis = this.terminalStatisticsChart.xAxes.push(new am4charts.CategoryAxis());
    categoryAxis.dataFields.category = "deviceType";
    categoryAxis.title.text = "Device Type";
    categoryAxis.title.fontSize = 12
    categoryAxis.renderer.grid.template.disabled = true;
    categoryAxis.renderer.labels.template.fontSize = 12;
    categoryAxis.renderer.minGridDistance = 20;
    categoryAxis.renderer.labels.template.disabled = false;

    let valueAxis = this.terminalStatisticsChart.yAxes.push(new am4charts.ValueAxis());
    valueAxis.title.text = "Terminals Configured";
    valueAxis.title.fontSize = 12
    valueAxis.renderer.labels.template.fontSize = 12;

    // Create series
    let series = this.terminalStatisticsChart.series.push(new am4charts.ColumnSeries());
    series.dataFields.valueY = "configuredDevicesCount";
    series.dataFields.categoryX = "deviceType";
    series.name = "Configured Devices";
    series.columns.template.tooltipText = "{categoryX}: [bold]{valueY}[/]";
    series.columns.template.fill = am4core.color("#4F84CF");
    series.columns.template.width = am4core.percent(40);
    series.columns.template.column.cornerRadiusTopLeft = 5;
    series.columns.template.column.cornerRadiusTopRight = 5;
  }

  initializeBoundTerminalsChart() {
    if (this.boundTerminalsChart) {
      this.boundTerminalsChart.dispose();
    }
    this.boundTerminalsChart = am4core.create("boundTerminalChartDiv", am4charts.XYChart);
    this.boundTerminalsChart.logo.disabled = true;
    this.boundTerminalsChart.data = this.boundTerminals;

    // Create axes
    let categoryAxis = this.boundTerminalsChart.xAxes.push(new am4charts.CategoryAxis());
    categoryAxis.dataFields.category = "day";
    categoryAxis.renderer.grid.template.location = 0;
    categoryAxis.renderer.cellStartLocation = 0.1;
    categoryAxis.renderer.cellEndLocation = 0.9;
    categoryAxis.renderer.grid.template.disabled = true;
    categoryAxis.renderer.minGridDistance = 20;
    categoryAxis.renderer.labels.template.fontSize = 12;

    let valueAxis = this.boundTerminalsChart.yAxes.push(new am4charts.ValueAxis());

    this.createSeries("bound", "Bound", "#54BE47");
    this.createSeries("unbound", "Unbound", "#F6AA1C");

  }


  // Create series
  createSeries(field: string, name: string, barColor: string) {
    let series = this.boundTerminalsChart.series.push(new am4charts.ColumnSeries());
    series.dataFields.valueY = field; // Switched to valueY
    series.dataFields.categoryX = "day"; // Switched to categoryX
    series.name = name;
    series.columns.template.tooltipText = "{name}: [bold]{valueY}[/]"; // Switched to valueY
    series.columns.template.width = am4core.percent(100);
    series.sequencedInterpolation = true;
    series.columns.template.fill = am4core.color(barColor);

    let valueLabel = series.bullets.push(new am4charts.LabelBullet());
    valueLabel.label.verticalCenter = "bottom"; // Adjusted to bottom
    valueLabel.label.dy = -10; // Adjusted for positioning
    valueLabel.label.hideOversized = false;
    valueLabel.label.truncate = false;

    let categoryLabel = series.bullets.push(new am4charts.LabelBullet());
    categoryLabel.label.horizontalCenter = "left"; // Adjusted to left
    categoryLabel.label.dx = 10; // Adjusted for positioning
    categoryLabel.label.fill = am4core.color("#fff");
    categoryLabel.label.hideOversized = false;
    categoryLabel.label.truncate = false;
  }

  private disposeChart(chart: any) {
    if (!chart) {
      return;
    }

    this.zone.runOutsideAngular(() => {
      chart.dispose();
    });
  }

  private renderAfterViewUpdate(callback: () => void): void {
    this.cdr.detectChanges();
    setTimeout(() => {
      callback();
    }, 0);
  }

  private finalizeDeviceChartState(): void {
    if (!this.hasBoundTerminalsLoaded || !this.hasUnboundTerminalsLoaded) {
      return;
    }

    this.isDevicesChartLoading = false;
    const hasData = this.dailyBoundTerminals.some((item) => (item.boundTerminals ?? 0) > 0) ||
      this.dailyUnboundTerminals.some((item) => (item.unboundTerminals ?? 0) > 0);

    this.isDevicesChartEmpty = !hasData;

    if (!hasData) {
      this.boundTerminals = [];
      this.disposeChart(this.boundTerminalsChart);
      this.boundTerminalsChart = undefined;
      this.cdr.markForCheck();
      return;
    }

    this.boundTerminals = this.mergeBoundandUnboundTerminalArrays(this.dailyBoundTerminals, this.dailyUnboundTerminals);
    if (this.boundTerminals.length > 0) {
      this.renderAfterViewUpdate(() => {
        am4core.ready(() => {
          this.zone.runOutsideAngular(() => {
            this.initializeBoundTerminalsChart();
          });
        });
      });
    }

    this.cdr.markForCheck();
  }

  handleDataFetchError(error: any) {
    this.isLoading = false;
    if (error.status !== 403) {
      this.alertService.warn('Error fetching data');
    } else {
      this.alertService.warn(error.responseMessage);
      setTimeout(() => {
        this.alertService.error(error);
      }, 2000);
    }
    this.cdr.markForCheck();
  }

  getAllTerminals() {
    this.terminalService.getTerminals(this.pageIndex, this.pageSize).subscribe(
      (res) => {
        this.allTerminals = res["data"];
        if (res["totalElements"]) {
          this.terminalTotalCount = res["totalElements"];
        } else {
          this.terminalTotalCount = 0;
        };
        this.isLoading = false;
        this.cdr.markForCheck();
      },
      (error) => {
        this.handleDataFetchError(error);
      }
    );
  }

  getAllOem() {
    this.oemService.getOemList(this.pageIndex, 10000).subscribe({
      next: (res) => {
        this.allOems = res.content;
        this.totalOemCount = res.totalElements;
        this.cdr.markForCheck();
      },
      error: (error) => {
        if (error.status !== 403) {
          this.isLoading = false;
          this.alertService.warn("Error fetching oem");
        } else {
          this.isLoading = false;
          this.alertService.warn(error.error.responseMessage);
          setTimeout(() => {
            this.alertService.error(error);
          }, 2000);
        }
        this.cdr.markForCheck();
      }
    });
  }

  getAllMerchants() {
    this.isLoading = true;
    this.merchantService.getMerchants(this.pageIndex, this.pageSize).subscribe(
      (res) => {
        this.allMerchants = res["data"].map(data => ({ ...data, showActions: false }));
        if (res["totalElements"]) {
          this.totalMerchantCount = res["totalElements"];
        } else {
          this.totalMerchantCount = 0;
        }
        this.isLoading = false;
        this.cdr.markForCheck();
      },
      (error) => {
        this.handleDataFetchError(error);
      }
    );
  }

  mergeBoundandUnboundTerminalArrays(boundTerminalsData: any[], unboundTerminalsData: any[]): any[] {
    const combinedData = boundTerminalsData.map(boundItem => {
      const correspondingUnboundItem = unboundTerminalsData.find(unboundItem => unboundItem.day === boundItem.day);

      return {
        day: boundItem.day,
        bound: boundItem.boundTerminals,
        unbound: correspondingUnboundItem ? correspondingUnboundItem.unboundTerminals : 0
      };
    });

    return combinedData;
  }

  ngOnDestroy() {
    this.zone.runOutsideAngular(() => {
      if (this.chart) {
        this.chart.dispose();
      }
      if (this.terminalStatisticsChart) {
        this.terminalStatisticsChart.dispose();
      }
      if (this.boundTerminalsChart) {
        this.boundTerminalsChart.dispose();
      }
    });
  }

  getAllApps() {
    this.isLoading = true;
    this.sysConfig.getApps(this.pageIndex, this.pageSize).subscribe(
      (res) => {
        this.isLoading = false;
        const payload = res as any;
        this.allApps = payload["data"] ?? [];
        if (payload["totalElements"]) {
          this.totalAppCount = payload["totalElements"];
        } else {
          this.totalAppCount = 0;
        }
        this.cdr.markForCheck();
      },
      (error) => {
        this.handleDataFetchError(error);
      }
    );
  }

  goToViewClients() {
    this.router.navigate(['/user/manage-clients/view-clients'])
  }

  goToViewApps() {
    this.router.navigate(['/user/manage-apps']);
  }

  getConfiguredDevicesByDeviceType() {
    this.disposeChart(this.terminalStatisticsChart);
    this.terminalStatisticsChart = undefined;
    this.isTerminalStatLoading = true;
    this.isTerminalChartEmpty = false;

    this.businessService.getConfiguredDevicesByDeviceType(this.days).subscribe({
      next: (res) => {
        this.isLoading = false;
        this.isTerminalStatLoading = false;
        const chartData = Array.isArray(res) ? res : [];
        this.configuredDeviceByDeviceType = chartData.map((item: any) => ({
          deviceType: item.deviceType ?? 'Unknown',
          configuredDevicesCount: item.configuredDevicesCount ?? 0,
        }));

        this.isTerminalChartEmpty = this.configuredDeviceByDeviceType.length === 0;
        if (!this.isTerminalChartEmpty) {
          this.renderAfterViewUpdate(() => {
            am4core.ready(() => {
              this.zone.runOutsideAngular(() => {
                this.initalizeTerminalStatisticsChart();
              });
            });
          });
        }
        this.cdr.markForCheck();
      },
      error: (error) => {
        this.isTerminalStatLoading = false;
        this.isTerminalChartEmpty = true;
        this.disposeChart(this.terminalStatisticsChart);
        this.terminalStatisticsChart = undefined;
        this.handleDataFetchError(error);
      }
    });
  }

  onDaysChange(event: Event) {
    const selectedValue = (event.target as HTMLSelectElement).value;
    this.days = parseInt(selectedValue, 10);
    this.getConfiguredDevicesByDeviceType();
  }

  getAppUsage() {
    this.businessService.getAppUsage().subscribe({
      next: (res) => {
        this.isLoading = false;
        this.allAppUsage = res;
        this.cdr.markForCheck();
      },
      error: (error) => {
        this.handleDataFetchError(error);
      }
    })
  }

  // Helper function to get the day of the week from a date
  getDayOfWeek(dateString: string): string {
    const date = new Date(dateString);
    const daysOfWeek = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
    return daysOfWeek[date.getDay()];
  }

  // Function to rotate the array based on the starting day
  rotateArray(arr: any[], startIndex: number): any[] {
    return arr.slice(startIndex).concat(arr.slice(0, startIndex));
  }

  mapConfiguredTerminals(data: any[], terminalType: string) {
    if (!data || data.length === 0) return [];

    // Identify the first date in the response, regardless of format
    const firstItem = data[0];
    const firstDate = firstItem.date || Object.keys(firstItem)[0];
    const firstDayOfWeek = this.getDayOfWeek(firstDate);

    const daysOfWeek = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
    const startIndex = daysOfWeek.indexOf(firstDayOfWeek);

    // Rotate days array to start with the first day of the response
    let allDays = [
      { day: "Sunday", [terminalType]: 0 },
      { day: "Monday", [terminalType]: 0 },
      { day: "Tuesday", [terminalType]: 0 },
      { day: "Wednesday", [terminalType]: 0 },
      { day: "Thursday", [terminalType]: 0 },
      { day: "Friday", [terminalType]: 0 },
      { day: "Saturday", [terminalType]: 0 }
    ];

    allDays = this.rotateArray(allDays, startIndex);

    // Map the data to the rotated allDays array
    data.forEach(item => {
      const date = item.date || Object.keys(item)[0];
      const value = item.count || item[date];
      const dayOfWeek = this.getDayOfWeek(date);

      const dayObj = allDays.find(d => d.day === dayOfWeek);
      if (dayObj) {
        dayObj[terminalType] = value;
      }
    });

    return allDays;
  }

  getWeeklyBoundDevices() {
    this.businessService.getWeeklyBoundDevice().subscribe(
      (res) => {
        this.hasBoundTerminalsLoaded = true;
        this.isLoading = false;
        this.dailyBoundTerminals = this.mapConfiguredTerminals(res, 'boundTerminals');
        this.finalizeDeviceChartState();
        this.cdr.markForCheck();
      },
      (error) => {
        this.hasBoundTerminalsLoaded = true;
        this.finalizeDeviceChartState();
        this.handleDataFetchError(error);
      }
    );
  }

  getWeeklyUnboundDevices() {
    this.businessService.getWeeklyUnboundDevice().subscribe(
      (res) => {
        this.hasUnboundTerminalsLoaded = true;
        this.isLoading = false;
        this.dailyUnboundTerminals = this.mapConfiguredTerminals(res, 'unboundTerminals');
        this.finalizeDeviceChartState();
        this.cdr.markForCheck();
      },
      (error) => {
        this.hasUnboundTerminalsLoaded = true;
        this.finalizeDeviceChartState();
        this.handleDataFetchError(error);
      }
    );
  }

}
